import { getDateNumber, getFullYear, getMonthNumber } from '@shared/utils';
import type { Timezone } from 'next-intl';
import { zonedLocalDateTimeToUtcString } from '../../../lottery-results/utils/buildResultsRangeParams';
import type { WalletTransaction } from '../types';
import { MONTH_KEY_LENGTH } from './constants';
import type { DayKey, MonthKey, TransactionsDayGroup } from './types';

function toUtcIsoString(zonedDateTime: string): string {
  return `${zonedDateTime}.000Z`;
}

function localIsoDateKey(date: Date, timeZone?: Timezone): DayKey {
  if (!timeZone) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}` as DayKey;
  }

  const year = getFullYear(date, timeZone);
  const month = String(getMonthNumber(date, timeZone) + 1).padStart(2, '0');
  const day = String(getDateNumber(date, timeZone)).padStart(2, '0');
  return `${year}-${month}-${day}` as DayKey;
}

function groupTransactionsByDay(transactions: WalletTransaction[], timeZone?: Timezone): TransactionsDayGroup[] {
  const map = new Map<DayKey, { day: DayKey; transactions: WalletTransaction[] }>();

  for (const t of transactions) {
    const date = new Date(t.date);
    const isoString = localIsoDateKey(date, timeZone);
    let group = map.get(isoString);
    if (!group) {
      group = { day: isoString, transactions: [] };
      map.set(isoString, group);
    }
    group.transactions.push(t);
  }

  return Array.from(map.values());
}

function monthKeyToDateRange(monthKey: string, timeZone?: Timezone): [string, string] {
  const parts = monthKey.split('-');
  const year = Number(parts[0]);
  const month = Number(parts[1]);

  if (!timeZone) {
    const from = new Date(year, month - 1, 1).toISOString();
    const to = new Date(year, month, 1).toISOString();
    return [from, to];
  }

  const from = toUtcIsoString(zonedLocalDateTimeToUtcString(year, month, 1, 0, 0, timeZone));
  const next = month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
  const to = toUtcIsoString(zonedLocalDateTimeToUtcString(next.year, next.month, 1, 0, 0, timeZone));
  return [from, to];
}

function monthKeyToDate(monthKey: MonthKey): Date {
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(year!, month! - 1, 1, 12);
}

function getLast12Months(timeZone?: Timezone): MonthKey[] {
  const now = new Date();
  const months: MonthKey[] = [];

  if (!timeZone) {
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      months.push(dateToMonthKey(date.toISOString()));
    }

    return months;
  }

  let year = getFullYear(now, timeZone);
  let month = getMonthNumber(now, timeZone);

  for (let i = 0; i < 12; i++) {
    months.push(`${year}-${String(month + 1).padStart(2, '0')}` as MonthKey);
    month -= 1;
    if (month < 0) {
      month = 11;
      year -= 1;
    }
  }

  return months;
}

function dateToMonthKey(isoDateString: string, timeZone?: Timezone): MonthKey {
  if (!timeZone) {
    return isoDateString.slice(0, MONTH_KEY_LENGTH) as MonthKey;
  }

  const date = new Date(isoDateString);
  const year = getFullYear(date, timeZone);
  const month = String(getMonthNumber(date, timeZone) + 1).padStart(2, '0');
  return `${year}-${month}` as MonthKey;
}

function getMonthFromKey(monthKey: MonthKey): string {
  return monthKey.split('-')[1]!;
}

export { dateToMonthKey, getLast12Months, getMonthFromKey, groupTransactionsByDay, localIsoDateKey, monthKeyToDate, monthKeyToDateRange };
